import { cn } from "@faire/prototype-playground/lib/utils";
import React from "react";

import { TILE_HOVER_GROUP, TILE_HOVER_IMAGE } from "./constants";
import { PriceLabel, type PriceMode } from "./PriceLabel";

export interface ProductCardData {
  token: string;
  name: string;
  imageUrl?: string;
  /**
   * The product's other photography — typically in-context / lifestyle shots
   * rather than the packshot in `imageUrl`. Already present on ~79% of the
   * catalog, so rows can break up a wall of same-angle, same-background tiles
   * without any new data (see `showLifestyleImage`).
   */
  galleryImageUrls?: string[];
  priceDollars?: number;
  msrpDollars?: number;
  caseSize?: number;
  isBestseller?: boolean;
  /** Shown above the name for cross-brand tiles (e.g. Better Together). */
  brandName?: string;
}

interface ProductCardProps {
  product: ProductCardData;
  priceMode: PriceMode;
  /** Hide the "Bestseller" badge even when the product is flagged (e.g. new brand). */
  showBestsellerBadge?: boolean;
  /**
   * When set, the card is a link (brand.com → Faire) instead of a gated button:
   * it navigates to `href` and does NOT trigger the sign-up modal.
   */
  href?: string;
  /** Optional content rendered between the product image and its name/price. */
  middleSlot?: React.ReactNode;
  /** On a tinted (brand-secondary) surface, drop the tile's grey image scrim so
   * the image sits directly on the surface instead of on a mismatched grey box. */
  onTintedSurface?: boolean;
  /** "square" (default) fits most product photography; "portrait" (2:3)
   * fits apparel brands' full-body model shots — see
   * `BrandDataset.productImageAspect`. */
  aspectRatio?: "square" | "portrait";
  /**
   * Render this tile from the product's lifestyle photography instead of its
   * packshot. Rows set this on a subset of their tiles so a row reads as mixed
   * content rather than a uniform product grid. Falls back to the packshot
   * when the product has no gallery, so a row never renders a hole.
   */
  showLifestyleImage?: boolean;
  /** Drop the name/price block — the brand.com surface is a showcase, and the
   * repeated text under every tile is what makes the grid feel like a
   * spreadsheet. */
  hideDetails?: boolean;
}

export const ProductCard: React.FC<ProductCardProps> = ({
  product,
  priceMode,
  showBestsellerBadge = true,
  href,
  middleSlot,
  onTintedSurface = false,
  aspectRatio = "square",
  showLifestyleImage = false,
  hideDetails = false,
}) => {
  // Lifestyle shot when asked for and available, packshot otherwise — the
  // fallback matters because gallery coverage is high but not universal.
  const lifestyleUrl = showLifestyleImage
    ? product.galleryImageUrls?.[0]
    : undefined;
  const displayUrl = lifestyleUrl ?? product.imageUrl;
  // The scrim exists to give white-background packshots an edge. A lifestyle
  // photo already has its own edges, so it would just muddy the image.
  const isLifestyle = Boolean(lifestyleUrl);

  const inner = (
    <>
      <div
        className={cn(
          "relative w-full overflow-hidden rounded",
          aspectRatio === "portrait" ? "aspect-[2/3]" : "aspect-square",
          !onTintedSurface && "bg-fs-surface-secondary"
        )}
      >
        {displayUrl ? (
          <img
            src={displayUrl}
            alt={product.name}
            className={cn("h-full w-full object-cover", TILE_HOVER_IMAGE)}
          />
        ) : null}
        {/* A faint dark scrim (~2-3% opacity) so product photos with pure white
            backgrounds get a little definition against the page's own white —
            applied across the board rather than detecting per-image background
            color. Dropped on tinted surfaces, which don't need the extra edge. */}
        {displayUrl && !onTintedSurface && !isLifestyle ? (
          <div
            className="pointer-events-none absolute inset-0"
            style={{ backgroundColor: "rgba(0,0,0,0.025)" }}
          />
        ) : null}
        {showBestsellerBadge && product.isBestseller ? (
          <span className="bg-fs-surface-primary type-fs-label text-fs-text-primary absolute top-2 left-2 rounded-[4px] px-2 py-1">
            Bestseller
          </span>
        ) : null}
      </div>
      {middleSlot}
      {hideDetails ? null : (
        <div className="flex flex-col gap-0.5">
          {product.brandName ? (
            <span className="type-fs-label text-fs-text-subdued">
              {product.brandName}
            </span>
          ) : null}
          <span
            className={cn(
              "type-fs-paragraph text-fs-text-primary",
              "line-clamp-2",
              // The image's lift reads as "this is alive"; the underline is what
              // says "this is a link". A photo growing slightly is ambiguous on
              // its own, especially on the tiles whose photo is the whole tile.
              "underline-offset-2 group-hover:underline"
            )}
          >
            {product.name}
          </span>
          <PriceLabel
            priceDollars={product.priceDollars}
            msrpDollars={product.msrpDollars}
            mode={priceMode}
          />
          {product.caseSize ? (
            <span className="type-fs-label text-fs-text-subdued">
              Case of {product.caseSize}
            </span>
          ) : null}
        </div>
      )}
    </>
  );

  // brand.com surface: link out to Faire (no sign-up gate).
  if (href) {
    return (
      <a
        href={href}
        className={cn("flex w-full flex-col gap-2 text-left", TILE_HOVER_GROUP)}
      >
        {inner}
      </a>
    );
  }

  return (
    <button
      type="button"
      data-signup-gate
      className={cn("flex w-full flex-col gap-2 text-left", TILE_HOVER_GROUP)}
    >
      {inner}
    </button>
  );
};
